import { logError } from './errors/log-utils'

export function groupBy<T, K extends keyof any>(
  items: T[] | undefined,
  keySelector: (item: T) => K,
): Record<K, T[]> {
  if (!items) return {} as Record<K, T[]>

  return items.reduce(
    (acc, item) => {
      const key = keySelector(item)
      if (!acc[key]) acc[key] = []
      acc[key].push(item)
      return acc
    },
    {} as Record<K, T[]>,
  )
}

export function tryRemoveBy<T>(arr: T[], predicate: (v: T) => boolean): boolean {
  const index = arr.findIndex(predicate)

  if (index == -1) {
    return false
  }
  arr.splice(index, 1)
  return true
}

export function removeBy<T>(arr: T[], predicate: (v: T) => boolean): void {
  const removed = tryRemoveBy(arr, predicate)
  if (!removed) {
    logError('Error: no match in array')
  }
}

export function tryRemove<T>(arr: T[], value: T): boolean {
  const index = arr.indexOf(value)

  if (index == -1) {
    return false
  }
  arr.splice(index, 1)
  return true
}

export function tryReplace<T>(arr: T[], from: T, to: T): boolean {
  const index = arr.indexOf(from)

  if (index === -1) {
    return false
  }

  arr[index] = to
  return true
}

export function replace<T>(arr: T[], from: T, to: T): void {
  const replaced = tryReplace(arr, from, to)
  if (!replaced) {
    logError('Error: value not found in array')
  }
}

export function replaced<T>(arr: T[], from: T, to: T): T[] {
  const index = arr.indexOf(from)
  if (index === -1) {
    throw new Error('Error: value not found in array')
  }
  return replacedAt(arr, index, to)
}

export function replacedAt<T>(arr: T[], index: number, to: T): T[] {
  const next = [...arr]
  next[index] = to
  return next
}

export function without<T>(arr: T[], value: T): T[] {
  const index = arr.indexOf(value)
  return [...arr.slice(0, index), ...arr.slice(index + 1)]
}

export function withoutArr<T>(arr: T[], toExclude: T[]): T[] {
  const result: T[] = []

  for (let i = 0; i < arr.length; i++) {
    const item = arr[i]
    let found = false

    for (let j = 0; j < toExclude.length; j++) {
      if (item === toExclude[j]) {
        found = true
        break
      }
    }

    if (!found) {
      result.push(item)
    }
  }

  return result
}

export function firstOrDefault<T>(arr: T[], _default: T): T {
  return first(arr) ?? _default
}

export function first<T>(arr: T[]): T | undefined {
  return arr.length > 0 ? arr[0] : undefined
}

export function entriesToMap<T, K extends PropertyKey, V>(
  items: T[],
  keySelector: (item: T) => K,
  valueSelector: (item: T) => V,
): Record<K, V> {
  const result = {} as Record<K, V>

  for (const item of items) {
    result[keySelector(item)] = valueSelector(item)
  }

  return result
}
